import React, { Fragment, useContext } from 'react';
import { Table } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Calculation } from '../../../../services/types/Calculator';
import { Operation } from '../../../../services/types/Operation';
import CalculatorVersion from './CalculatorVersion';
import { Button } from '../../../../shared/components';
import NumberFormatterSinSeparador from './NumberFormatterSinSeparador';
import MainContext from '../../../App/MainProvider/context';
import calculatorService from '../../../../services/calculatorService';

interface Props {
  operation: Operation;
  calculators: Calculation[];
  // availableLanguages: { toolkitId: number; languages: CheckValue[] }[];
  // handleChange: (id: string, value: boolean, toolkitId: number) => void;
  // handleDownloadClick: (toolkitId: number) => void;
  handleCalculateClick?: (rowId: number) => void;
  handleDeleteClick?: (rowId: number) => void;
  handleSendClick?: (operationId: number) => void;
  setLoadingSpinner: (isLoading: boolean) => void;
  isbloked?: boolean;
  className?: string;
}

const VersionTable = (props: Props) => {
  const {
    operation,
    calculators,
    className,
    handleCalculateClick,
    handleDeleteClick,
    handleSendClick,
    setLoadingSpinner,
    isbloked,
  } = props;
  const { t } = useTranslation();

  const isAddButtonFuelDisabled =
    calculators === null ||
    calculators.length === 0 ||
    calculators.some((calculator) => calculator.status !== 'FINISHED');

  const isSentCalculator =
    calculators === null ||
    calculators.length === 0 ||
    calculators.some((calculator) => calculator.sent === true);

  const { notify, service } = useContext(MainContext);

  function sumConstructionValues() {
    const validValues = calculators.filter(
      (calculator) => calculator.construction !== null && calculator.construction
    );

    if (validValues.length === 0) {
      return null;
    }
    // Sumar los valores válidos redondeando cada uno a 2 decimales primero
    return validValues.reduce((total, calculation) => {
      const constructionValue = calculation.construction || 0;
      const roundedValue = Math.round(constructionValue * 100) / 100;
      return total + roundedValue;
    }, 0);
  }

  function sumOperationValues() {
    const validValues = calculators.filter(
      (calculator) => calculator.operation !== null && calculator.operation
    );

    if (validValues.length === 0) {
      return null;
    }

    // Sumar los valores válidos redondeando cada uno a 2 decimales primero
    return validValues.reduce((total, calculation) => {
      const operationValue = calculation.operation || 0;
      const roundedValue = Math.round(operationValue * 100) / 100;
      return total + roundedValue;
    }, 0);
  }

  function sumNormalizedValues() {
    const validValues = calculators.filter((calculator) =>
      calculator.normalized !== null ? calculator.normalized : ''
    );

    if (validValues.length === 0) {
      return null;
    }
    // Sumar los valores válidos redondeando cada uno a 2 decimales primero
    return validValues.reduce((total, calculation) => {
      const normalizedValue = calculation.normalized || 0;
      const roundedValue = Math.round(normalizedValue * 100) / 100;
      return total + roundedValue;
    }, 0);
  }
  // Asignar el resultado de sumConstructionValues a una constante
  const totalConstruction = sumConstructionValues();
  const totalOperation = sumOperationValues();
  const totalNormalized = sumNormalizedValues();

  const renderRows = calculators.map((calc) => (
    <Fragment key={calc.id}>
      <CalculatorVersion
        key={`tv_${calc.version}`}
        calculation={calc}
        operation={operation}
        isSent={isSentCalculator}
        // pdfOptions={}
        // handleChange={(id, value) => handleChange(id, value, toolkit.id)}
        // handleDownloadClick={handleDownloadClick}
        handleCalculateClick={handleCalculateClick}
        handleDeleteClick={handleDeleteClick}
        setLoadingSpinner={setLoadingSpinner}
      />
    </Fragment>
  ));

  const downloadTotals = async (operationId: number, version?: number) => {
    try {
      setLoadingSpinner(true);
      await service(() => calculatorService.downloadDocumentTotals(operationId, version), {});
    } catch (error) {
      notify('Error', 'Failed to download document', 'error');
      // eslint-disable-next-line no-console
      console.error('Error downloading document:', error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className={className}>
      <div className="table-wrapper sticky-section-calculator w-100">
        <Table className="calculator_table">
          <colgroup>
            <col style={{ width: '15%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '5%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '5%' }} />
            <col style={{ width: '6%' }} />
            <col style={{ width: '5%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Sector</th>
              <th>Country</th>
              <th>Units</th>
              <th>Last Modification</th>
              <th>Modified By</th>
              <th>Construction</th>
              <th>Operation</th>
              <th>Normalized</th>
              <th>{!isSentCalculator ? 'State' : ''}</th>
              <th colSpan={2} className="text-right">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>{renderRows}</tbody>
          <tbody>
            <tr>
              <td>Total</td>
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <td colSpan={4} />
              <td>
                <NumberFormatterSinSeparador value={totalConstruction} />
              </td>
              <td>
                <NumberFormatterSinSeparador value={totalOperation} />
              </td>
              <td>
                <NumberFormatterSinSeparador value={totalNormalized} />
              </td>
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <td />
              <td colSpan={2} className="text-right">
                {!isSentCalculator && handleCalculateClick && (
                  <Button
                    key="btnNewVersionFromCurrent"
                    className="btn btn-primary py-1 btn-calc-send"
                    onClick={() => {
                      if (handleSendClick) {
                        handleSendClick(operation.id);
                      }
                    }}
                    text={t('sendCalculation')}
                    disabled={isAddButtonFuelDisabled || isbloked}
                  />
                )}
                {isSentCalculator &&
                  (totalConstruction !== null ||
                    totalOperation !== null ||
                    totalNormalized !== null) && (
                    <Button
                      key={`tv_btn_download_${operation.id}`}
                      className="btn-tertiary no-padding action-calculator"
                      htmlId={`tv_btn_download_${operation.id}`}
                      icon="file-download"
                      text={t('')}
                      onClick={() => downloadTotals(operation.id, calculators[0]?.version)}
                    />
                  )}
              </td>
            </tr>
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default VersionTable;
